import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { PublicLinksProvider } from "./context/PublicLinksContext";

// Layout components (needed on first paint)
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";
import ProtectedRoute from "./components/common/ProtectedRoute";
import LoadingSpinner from "./components/common/LoadingSpinner";

// Home stays eager — it is the landing page
import Home from "./pages/Home";

// Everything else is code-split so it isn't shipped to homepage visitors
const TokenResolver = lazy(() => import("./pages/TokenResolver"));

// Public content pages — also exposed at stable, SEO-friendly URLs
const About = lazy(() => import("./pages/About"));
const Courses = lazy(() => import("./pages/Courses"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));

// Interactive "Learning Python" lessons
const LearnPython = lazy(() => import("./learn/LearnPython"));
const LessonPage = lazy(() => import("./learn/LessonPage"));

// Admin pages (heavy, never needed by public visitors)
const AdminLogin = lazy(() => import("./admin/AdminLogin"));
const AdminLayout = lazy(() => import("./admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const ManageCourses = lazy(() => import("./admin/ManageCourses"));
const ManageInstructors = lazy(() => import("./admin/ManageInstructors"));
const AdminCourseDetail = lazy(() => import("./admin/CourseDetail"));
const AdminProfile = lazy(() => import("./admin/AdminProfile"));

const NotFound = lazy(() => import("./pages/NotFound"));
const RecaptchaProvider = lazy(() => import("./components/common/RecaptchaProvider"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

// Wrapper that adds AdminLayout to admin pages
const AdminPage = ({ children }) => (
  <ProtectedRoute userType="admin">
    <AdminLayout>{children}</AdminLayout>
  </ProtectedRoute>
);

function App() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <Suspense fallback={<PageLoader />}>
      <Routes>
      {/* Admin routes - separate layout, no public header/footer */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <AdminPage>
            <AdminDashboard />
          </AdminPage>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <AdminPage>
            <AdminProfile />
          </AdminPage>
        }
      />
      <Route
        path="/admin/courses"
        element={
          <AdminPage>
            <ManageCourses />
          </AdminPage>
        }
      />
      <Route
        path="/admin/courses/:id"
        element={
          <AdminPage>
            <AdminCourseDetail />
          </AdminPage>
        }
      />
      <Route
        path="/admin/instructors"
        element={
          <AdminPage>
            <ManageInstructors />
          </AdminPage>
        }
      />

      {/* Public routes - with public header/footer */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex flex-col">
            <PublicLinksProvider>
              <Header />
              <main className="flex-grow">
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes (home stays clean) */}
                  <Route path="/" element={<Home />} />

                  {/* Stable, SEO-friendly URLs for the public content pages.
                      These take precedence over the /:token resolver below. */}
                  <Route path="/about" element={<About />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/programs" element={<Courses />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/learn-python" element={<LearnPython />} />
                  <Route path="/learn-python/:slug" element={<LessonPage />} />
                  <Route path="/learn-python/:slug/:topic" element={<LessonPage />} />
                  <Route
                    path="/contact"
                    element={
                      <RecaptchaProvider>
                        <Contact />
                      </RecaptchaProvider>
                    }
                  />

                  <Route path="/:token" element={<TokenResolver />} />

                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </main>
              <Footer />
            </PublicLinksProvider>
          </div>
        }
      />
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
